/**
 * Vocabulário do Mapa da Produção: estados, rótulos, tons e as ações que cada
 * estado aceita — de ambiente, de tarefa e de PEÇA DESMEMBRADA (v3).
 *
 * As transições espelham o backend. O backend continua sendo quem decide
 * (responde 409 em transição inválida); aqui elas servem para a tela não
 * oferecer um botão que certamente vai falhar.
 *
 * Tom segue a paleta canônica: cor só comunica estado.
 */
import { isBeforeToday, startOfDay, toDate } from "../../../utils/status";

/**
 * Só três: ver, operar (ambiente/tarefa/peça) e gerir (cadastros E o desenho
 * da planta). A v2 tinha `mapa_producao:editar_mapa` para o desenho; ela saiu
 * do catálogo com o /layout (permissoes/migrations/0041), e pedir por ela aqui
 * esconderia o modo Organizar de todo mundo que não é Master.
 */
export const PERMISSAO = Object.freeze({
  VER: "mapa_producao:view",
  OPERAR: "mapa_producao:operar",
  GERIR: "mapa_producao:gerir",
});

// ---------------------------------------------------------- prioridade

/**
 * Prioridade do ambiente e da peça. O gestor escolhe; quando não escolheu, é
 * DERIVADA do prazo (`combinar.js` marca a origem em `origemPrioridade`, para
 * a tela poder dizer "estimada pelo prazo" em vez de mentir que alguém
 * decidiu — ADR-0059, rótulo tem que dizer a régua).
 */
export const PRIORIDADE = Object.freeze({
  NORMAL: "normal",
  ALTA: "alta",
  URGENTE: "urgente",
});

export const PRIORIDADES = Object.freeze([PRIORIDADE.NORMAL, PRIORIDADE.ALTA, PRIORIDADE.URGENTE]);

export const ROTULO_PRIORIDADE = Object.freeze({
  normal: "Normal",
  alta: "Alta",
  urgente: "Urgente",
});

export const TOM_PRIORIDADE = Object.freeze({
  normal: "neutral",
  alta: "warning",
  urgente: "danger",
});

export const ORIGEM_PRIORIDADE = Object.freeze({
  /** Alguém escolheu na tela. */
  APP: "app",
  /** Ninguém escolheu: saiu do prazo (atrasada = urgente, perto = alta). */
  PRAZO: "prazo",
});

// ------------------------------------------------------------ ambiente

export const STATUS_AMBIENTE = Object.freeze({
  AGUARDANDO: "aguardando",
  EM_PRODUCAO: "em_producao",
  PAUSADO: "pausado",
});

export const ROTULO_STATUS_AMBIENTE = Object.freeze({
  aguardando: "Aguardando",
  em_producao: "Em produção",
  pausado: "Pausado",
});

export const TOM_STATUS_AMBIENTE = Object.freeze({
  aguardando: "neutral",
  em_producao: "info",
  pausado: "warning",
});

export const ACAO_AMBIENTE = Object.freeze({
  INICIAR: "iniciar",
  PAUSAR: "pausar",
  RETOMAR: "retomar",
  AGUARDAR: "aguardar",
});

const ROTULO_ACAO_AMBIENTE = {
  iniciar: "Iniciar produção",
  pausar: "Pausar",
  retomar: "Retomar",
  aguardar: "Voltar para aguardando",
};

// Espelha `TRANSICOES` de mapa_producao/ambientes.py. Pausar da fila vale:
// "nem começou, falta material".
const ACOES_POR_STATUS_AMBIENTE = {
  aguardando: ["iniciar", "pausar"],
  em_producao: ["pausar", "aguardar"],
  pausado: ["retomar", "aguardar"],
};

/**
 * Ações de status que o ambiente aceita agora. Sem `podeOperar`, nenhuma.
 * Cada item: {acao, rotulo, pedeMotivo} — `pedeMotivo` = o modal oferece
 * o campo (opcional) de motivo, como na pausa.
 */
export function acoesDoAmbiente(statusApp, { podeOperar = false } = {}) {
  if (!podeOperar) return [];
  return (ACOES_POR_STATUS_AMBIENTE[statusApp] || []).map((acao) => ({
    acao,
    rotulo: ROTULO_ACAO_AMBIENTE[acao],
    pedeMotivo: acao === ACAO_AMBIENTE.PAUSAR,
  }));
}

// -------------------------------------------------------------- tarefa

export const STATUS_TAREFA = Object.freeze({
  AGUARDANDO: "aguardando",
  EM_ANDAMENTO: "em_andamento",
  PAUSADA: "pausada",
  CONCLUIDA: "concluida",
  CANCELADA: "cancelada",
});

export const STATUS_TAREFA_ABERTOS = Object.freeze(["aguardando", "em_andamento", "pausada"]);

export const ROTULO_STATUS_TAREFA = Object.freeze({
  aguardando: "Aguardando",
  em_andamento: "Em andamento",
  pausada: "Pausada",
  concluida: "Concluída",
  cancelada: "Cancelada",
});

export const TOM_STATUS_TAREFA = Object.freeze({
  aguardando: "neutral",
  em_andamento: "info",
  pausada: "warning",
  concluida: "success",
  cancelada: "neutral",
});

export const ACAO_TAREFA = Object.freeze({
  INICIAR: "iniciar",
  PAUSAR: "pausar",
  RETOMAR: "retomar",
  CONCLUIR: "concluir",
  CANCELAR: "cancelar",
  REABRIR: "reabrir",
});

const ROTULO_ACAO_TAREFA = {
  iniciar: "Iniciar",
  pausar: "Pausar",
  retomar: "Retomar",
  concluir: "Concluir",
  cancelar: "Cancelar tarefa",
  reabrir: "Reabrir",
};

const ACOES_POR_STATUS_TAREFA = {
  aguardando: ["iniciar", "concluir", "cancelar"],
  em_andamento: ["pausar", "concluir", "cancelar"],
  pausada: ["retomar", "concluir", "cancelar"],
  concluida: ["reabrir"],
  cancelada: ["reabrir"],
};

// Cancelar e reabrir mexem no que outro gestor já deu por encerrado.
const ACOES_DE_GESTAO = new Set(["cancelar", "reabrir"]);

/**
 * Ações que a tarefa aceita no estado atual, já filtradas pela permissão.
 * Cada item: {acao, rotulo, exigeMotivo, pedeMotivo, perigosa}.
 * `exigeMotivo`: o backend recusa sem motivo (cancelar).
 * Cancelar e reabrir pedem `gerir` ALÉM de `operar`: o POST /acao passa
 * antes pela permissão de operar (views_tarefas.py) — só `gerir` daria 403.
 */
export function acoesDaTarefa(status, { podeOperar = false, podeGerir = false } = {}) {
  if (!podeOperar) return [];
  return (ACOES_POR_STATUS_TAREFA[status] || [])
    .filter((acao) => !ACOES_DE_GESTAO.has(acao) || podeGerir)
    .map((acao) => ({
      acao,
      rotulo: ROTULO_ACAO_TAREFA[acao],
      exigeMotivo: acao === ACAO_TAREFA.CANCELAR,
      pedeMotivo: acao === ACAO_TAREFA.PAUSAR || acao === ACAO_TAREFA.CANCELAR,
      perigosa: acao === ACAO_TAREFA.CANCELAR,
    }));
}

export function tarefaAberta(tarefa) {
  return STATUS_TAREFA_ABERTOS.includes(tarefa?.status);
}

/** Aberta e com data limite ("YYYY-MM-DD", lida como data local) já passada. */
export function tarefaAtrasada(tarefa, hoje = new Date()) {
  if (!tarefaAberta(tarefa)) return false;
  const limite = toDate(tarefa.data_limite);
  return Boolean(limite) && isBeforeToday(limite, hoje);
}

// ---------------------------------------------------------------- peça

/**
 * PEÇA DESMEMBRADA (v3): o gestor tira uma peça do ambiente ("porta do
 * armário") e ela passa a andar sozinha pelos setores, com status, prazo,
 * responsável e tarefas próprios. Ela é DERIVADA do ambiente: nasce herdando
 * prioridade e prazos dele e pode voltar para ele.
 *
 * Os três primeiros estados são "fora do ambiente" (a peça está circulando);
 * os três últimos encerram o desmembramento.
 */
export const STATUS_PECA = Object.freeze({
  AGUARDANDO: "aguardando",
  EM_PRODUCAO: "em_producao",
  PAUSADA: "pausada",
  /** Voltou para o ambiente: a peça foi reintegrada. */
  RETORNADA: "retornada",
  /** Terminou sem voltar (foi direto para a obra, por exemplo). */
  CONCLUIDA: "concluida",
  /** Desmembramento cancelado (erro de digitação, peça que não existia). */
  CANCELADA: "cancelada",
});

/** Estados em que a peça está FORA do ambiente (é o que o ambiente conta). */
export const STATUS_PECA_FORA = Object.freeze(["aguardando", "em_producao", "pausada"]);

export const ROTULO_STATUS_PECA = Object.freeze({
  aguardando: "Aguardando",
  em_producao: "Em produção",
  pausada: "Pausada",
  retornada: "Voltou para o ambiente",
  concluida: "Concluída",
  cancelada: "Cancelada",
});

export const TOM_STATUS_PECA = Object.freeze({
  aguardando: "neutral",
  em_producao: "info",
  pausada: "warning",
  retornada: "success",
  concluida: "success",
  cancelada: "neutral",
});

export const ACAO_PECA = Object.freeze({
  INICIAR: "iniciar",
  PAUSAR: "pausar",
  RETOMAR: "retomar",
  AGUARDAR: "aguardar",
  RETORNAR: "retornar",
  CONCLUIR: "concluir",
  CANCELAR: "cancelar",
  REABRIR: "reabrir",
});

const ROTULO_ACAO_PECA = {
  iniciar: "Iniciar produção",
  pausar: "Pausar",
  retomar: "Retomar",
  aguardar: "Voltar para aguardando",
  retornar: "Voltou para o ambiente",
  concluir: "Concluir sem retornar",
  cancelar: "Cancelar desmembramento",
  reabrir: "Reabrir",
};

const ACOES_POR_STATUS_PECA = {
  aguardando: ["iniciar", "pausar", "retornar", "concluir", "cancelar"],
  em_producao: ["pausar", "aguardar", "retornar", "concluir", "cancelar"],
  pausada: ["retomar", "aguardar", "retornar", "concluir", "cancelar"],
  retornada: ["reabrir"],
  concluida: ["reabrir"],
  cancelada: ["reabrir"],
};

// Cancelar e reabrir mexem no que outro gestor já deu por encerrado.
const ACOES_DE_GESTAO_PECA = new Set(["cancelar", "reabrir"]);

/**
 * Ações que a peça aceita agora, já filtradas pela permissão. Cada item:
 * {acao, rotulo, exigeMotivo, pedeMotivo, perigosa}. `exigeMotivo` = o backend
 * recusa sem motivo (cancelar o desmembramento).
 */
export function acoesDaPeca(status, { podeOperar = false, podeGerir = false } = {}) {
  if (!podeOperar) return [];
  return (ACOES_POR_STATUS_PECA[status] || [])
    .filter((acao) => !ACOES_DE_GESTAO_PECA.has(acao) || podeGerir)
    .map((acao) => ({
      acao,
      rotulo: ROTULO_ACAO_PECA[acao],
      exigeMotivo: acao === ACAO_PECA.CANCELAR,
      pedeMotivo: acao === ACAO_PECA.PAUSAR || acao === ACAO_PECA.CANCELAR,
      perigosa: acao === ACAO_PECA.CANCELAR,
    }));
}

/** A peça está circulando fora do ambiente. */
export function pecaFora(peca) {
  return STATUS_PECA_FORA.includes(peca?.status);
}

/** Fora do ambiente e com data limite já passada. */
export function pecaAtrasada(peca, hoje = new Date()) {
  if (!pecaFora(peca)) return false;
  const limite = toDate(peca.data_limite);
  return Boolean(limite) && isBeforeToday(limite, hoje);
}

// Prazo de hoje até este tanto de dias conta como "alta": é a janela em que o
// gestor ainda consegue remanejar gente sem furar a entrega.
const DIAS_PARA_PRIORIDADE_ALTA = 3;
const MS_POR_DIA = 24 * 60 * 60 * 1000;

/**
 * Prioridade deduzida do prazo, para quem não escolheu nenhuma: atrasada é
 * urgente, vence em até três dias é alta, o resto é normal. Sem prazo, normal.
 */
export function prioridadeDerivada(prazo, hoje = new Date()) {
  // `startOfDay(undefined)` devolve HOJE (o default do helper da casa): sem o
  // `toDate` antes, ambiente sem prazo virava "alta" — prioridade inventada.
  const limite = startOfDay(toDate(prazo));
  const dia = startOfDay(hoje);
  if (!limite || !dia) return PRIORIDADE.NORMAL;
  if (isBeforeToday(limite, hoje)) return PRIORIDADE.URGENTE;
  const dias = Math.round((limite - dia) / MS_POR_DIA);
  return dias <= DIAS_PARA_PRIORIDADE_ALTA ? PRIORIDADE.ALTA : PRIORIDADE.NORMAL;
}
